// goblic-guard's request/decision contract — see
// docs/plans/2026-09-30-runners/02-guard-en-claude-code.md §2.1.
//
// This is the general form every runner (or test) that talks to the guard
// uses: a tool call comes in on stdin (or over the Goblic channel's
// `guard.check` op — see ./channel.ts), and the worker answers with a plain
// `allow`/`block` decision. `ask` only ever happens between the worker and
// the guard's own wait loop (up to 15 minutes, 04 §2.6) — by the time an
// answer reaches the runner it has already resolved to `allow` or `block`,
// so no runner needs its own waiting logic.

import { z } from 'zod'

/** Which runner is asking — the only two kinds that go through the guard (02 §2.4). */
export const GuardRunnerSchema = z.enum(['claude-cli', 'pi'])
export type GuardRunner = z.infer<typeof GuardRunnerSchema>

export const GuardRequestSchema = z.object({
  runner: GuardRunnerSchema,
  /** The runner's own id for this call, when it has one (e.g. Claude Code's `tool_use_id`). */
  toolCallId: z.string().min(1).optional(),
  tool: z.string().min(1),
  input: z.unknown(),
  /** Present when the call comes from a sub-task (06); the guard applies its stricter rules. */
  subtaskId: z.string().min(1).optional(),
})
export type GuardRequest = z.infer<typeof GuardRequestSchema>

export const GuardDecisionSchema = z.object({
  decision: z.enum(['allow', 'block']),
  /** Always present on `block`; the guard fails closed with a reason on any error too. */
  reason: z.string().min(1).optional(),
})
export type GuardDecision = z.infer<typeof GuardDecisionSchema>

/** Throwing parse — use when reading a guard request off stdin/the channel. */
export function parseGuardRequest(input: unknown): GuardRequest {
  return GuardRequestSchema.parse(input)
}

/** Non-throwing parse — use to fail closed on malformed input instead of throwing. */
export function safeParseGuardRequest(input: unknown) {
  return GuardRequestSchema.safeParse(input)
}

/** Throwing parse — use when reading the worker's decision back in the guard binary/extension. */
export function parseGuardDecision(input: unknown): GuardDecision {
  return GuardDecisionSchema.parse(input)
}

export function safeParseGuardDecision(input: unknown) {
  return GuardDecisionSchema.safeParse(input)
}
